import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Input, Select } from '../../../../components';
import { ReportService } from '../../../../services';
import SelectRoute from '../../panel/select-route';
import SelectClient from './select-client';
import SelectDriver from './select-driver';
import SelectVehicle from './select-vehicle';

const ModalCreateIncident = ({ onClose, onSuccess }) => {
  const { form, isLoading, onChange, selects, submit } = useForm({ onClose, onSuccess });

  return (
    <div className="w-[48rem] bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4">
        <h2 className="text-lg font-bold text-text">Reportar incidencia</h2>
        <button
          type="button"
          className="p-1.5 rounded-full hover:bg-gray5 transition-colors"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-text">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 px-6">
        <div>
          <p className="mb-1 text-text">Nombre del Cliente</p>
          <SelectClient
            value={form.user_id}
            onSelect={item => onChange('user_id', item?.id)}
          />
        </div>
        <div>
          <p className="mb-1 text-text">Ruta</p>
          <SelectRoute
            value={form.route_id}
            onSelect={item => onChange('route_id', item?.id)}
          />
        </div>
        <div>
          <p className="mb-1 text-text">Nombre del Conductor</p>
          <SelectDriver
            value={form.driver_id}
            onSelect={item => {
              onChange('driver_id', item?.id);
              onChange('vehicle_id', item?.person?.driver?.vehicle_id);
              onChange('vehicle_plaque', item?.person?.driver?.vehicle?.plaque);
            }}
          />
        </div>
        <div>
          <p className="mb-1 text-text">Vehículo</p>
          <SelectVehicle
            value={form.vehicle_id}
            placeholder="Placa"
            selectedLabel={form.vehicle_plaque}
            onSelect={item => {
              onChange('vehicle_id', item?.id);
              onChange('vehicle_plaque', item?.plaque);
            }}
          />
        </div>
        <div>
          <p className="mb-1 text-text">Incidencia (tipo)</p>
          <Select
            value={form.incident_type_id}
            items={selects.incidentTypes}
            onValueChange={v => onChange('incident_type_id', v)}
          />
        </div>
        <Input
          label="Resultado"
          value={form.result}
          onValueChange={v => onChange('result', v)}
        />
        <div>
          <p className="mb-1 text-text">Afectó al servicio</p>
          <Select
            value={form.is_affected}
            items={selects.isAffected}
            onValueChange={v => onChange('is_affected', v)}
          />
        </div>
        <div className="col-span-2">
          <p className="mb-1 text-text">Observaciones (opcional)</p>
          <textarea
            className="border border-gray6 w-full rounded-lg px-5 py-3"
            label="Observaciones (opcional)"
            value={form.company?.address_house}
            onChange={e => onChange('observations', e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-center mt-2 px-6 py-4 gap-3">
        <button
          type="button"
          className="px-6 py-2 bg-gray-200 text-text font-semibold rounded-full hover:bg-gray-300 transition-colors text-sm"
          onClick={onClose}
          disabled={isLoading}
        >
          Cerrar
        </button>
        <button
          type="button"
          className="px-6 py-2 bg-red text-white font-semibold rounded-full hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
          onClick={submit}
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
};

const useForm = ({ onClose, onSuccess }) => {
  const initialState = {
    user_id: '',
    driver_id: '',
    vehicle_id: '',
    vehicle_plaque: '',
    route_id: '',
    incident_type_id: '',
    is_affected: '',
    result: '',
    observations: '',
  };

  const [form, setForm] = useState(initialState);
  const [selects, setSelects] = useState({
    routes: [],
    incidentTypes: [],
    isAffected: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const getSelects = async () => {
    const format = (i) => i?.map(x => ({ value: x.id, label: x.name }));

    try {
      const response = await ReportService.moderator.incidents.getSelects();
      setSelects(s => ({
        ...s,
        incidentTypes: format(response.incidentTypes),
        isAffected: format(response.isAffected),
      }));

    } catch (error) {
      setSelects({ states: [] });
    }
  };

  const onChange = (target, value) => {
    setForm(s => ({ ...s, [target]: value }));
  };

  const isValidForm = () => {
    const onError = (msg) => {
      toast.error(msg);
      return false;
    }

    if (!form.user_id)
      return onError('El cliente es requerido');

    if (!form.route_id)
      return onError('La ruta es requerida');

    if (!form.driver_id)
      return onError('El conductor es requerido');

    if (!form.vehicle_id)
      return onError('El vehículo es requerido');

    if (!form.incident_type_id)
      return onError('El tipo de incidencia es requerido');

    if (!form.result)
      return onError('El resultado es requerido');

    if (form.is_affected === '')
      return onError('Debe indicar si afectó el servicio o no');

    return true;
  };

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);
    try {
      await ReportService.moderator.incidents.create(form);
      toast.success('Incidencia creada con éxito');
      onSuccess?.();
      onClose?.();

    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSelects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    form,
    isLoading,
    onChange,
    selects,
    submit,
  };
};

export default ModalCreateIncident;
